import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useMaybeAssignCategoryTreeExperiment } from "@faire/retailer-visitor-shared/lib/useMaybeAssignCategoryTreeExperiment";
import { useNavigationQuery } from "@faire/retailer-visitor-shared/stores/domain/useNavigationQuery";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Weight } from "@faire/web--source/slate/Font/utils";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Span, Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { Link } from "@faire/web--source/ui/LinkAnchor";
import { trackCategoriesSideNavigationClick } from "@faire/web-api--source/events/categories/click/sideNavigation";
import { IRecordCategoryNodesVisibilityRequest } from "@faire/web-api--source/indigofair/category/IRecordCategoryNodesVisibilityRequest";
import { ClientPlatformType } from "@faire/web-api--source/indigofair/data/ClientPlatformType";
import flatMap from "lodash/flatMap";
import uniq from "lodash/uniq";
import * as React from "react";
import { useMemo } from "react";
import styled, { css } from "styled-components";
var FEATURED_NAV_GROUP_UNIQUE_ID = "/nav_link/featured";
/**
 * Create structure for each leaf link
 */ var convertSublinkToLeafLink = function(navLink) {
    var _navLink_name, _navLink_deep_link_path;
    return {
        name: (_navLink_name = navLink.name) !== null && _navLink_name !== void 0 ? _navLink_name : "",
        to: (_navLink_deep_link_path = navLink.deep_link_path) !== null && _navLink_deep_link_path !== void 0 ? _navLink_deep_link_path : "",
        pathIdentifier: navLink.path_identifier,
        isVisible: navLink.style_group !== "INVISIBLE" && navLink.style_group !== "INVISIBLE_FOR_ASSIGNMENT",
        shouldRecordPathIdentifier: navLink.style_group !== "INVISIBLE"
    };
};
/**
 * Creates structure for each group header navlink that contains leaf links
 */ var convertSublinkToLinkSection = function(navLink) {
    var name = navLink.name;
    return {
        sectionHeader: name !== null && name !== void 0 ? name : "",
        links: navLink.sublinks.map(convertSublinkToLeafLink),
        pathIdentifier: navLink.path_identifier
    };
};
/**
 * Create list of navlink sections
 */ var getFeaturedNavLinkSections = function(navLinkGroups) {
    var _navLinkGroups_find;
    // the Featured navlink will be the first one in the Featured group's navlinks list
    var featuredNavLink = (_navLinkGroups_find = navLinkGroups.find(function(group) {
        return group.unique_identifier === FEATURED_NAV_GROUP_UNIQUE_ID;
    })) === null || _navLinkGroups_find === void 0 ? void 0 : _navLinkGroups_find.navigation_links[0];
    if (!featuredNavLink) {
        return null;
    }
    return featuredNavLink.sublinks.map(convertSublinkToLinkSection);
};
export var PrunedFeaturedSideNavigation = function() {
    var navigationLinkGroup = useNavigationQuery(ClientPlatformType.CLIENT_DESKTOP).navigationLinkGroup;
    var linkSections = getFeaturedNavLinkSections(navigationLinkGroup);
    var maybeAssignCategoryTreeExperiment = useMaybeAssignCategoryTreeExperiment();
    var pathIdentifiersToRecord = useMemo(function() {
        return uniq(flatMap(linkSections, function(section) {
            return [
                section.pathIdentifier
            ].concat(_to_consumable_array(section.links.map(function(link) {
                return link.shouldRecordPathIdentifier ? link.pathIdentifier : undefined;
            })));
        })).filter(isNotUndefined);
    }, [
        linkSections
    ]);
    var onVisible = useDoActionWhenVisible(function() {
        maybeAssignCategoryTreeExperiment(function() {
            return pathIdentifiersToRecord;
        }, IRecordCategoryNodesVisibilityRequest.Source.WEB_DESKTOP_LEFT_NAV);
    });
    if (!linkSections || linkSections.length === 0) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Container, {
        "data-test-id": "category-page-sidebar",
        ref: onVisible,
        children: linkSections.map(function(section) {
            return /*#__PURE__*/ _jsx(CollectionsCategorySection, {
                linkSection: section
            }, section.sectionHeader);
        })
    });
};
var Container = styled.nav.withConfig({
    componentId: "sc-fdf31aac-0"
})([
    "margin-bottom:",
    ";"
], getSpacing("1x"));
var CollectionsCategorySection = function(param) {
    var linkSection = param.linkSection;
    var sectionHeader = linkSection.sectionHeader, links = linkSection.links;
    var listHeaderId = "category-list-header-".concat(sectionHeader.toLowerCase().replace(" ", "-"));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            sectionHeader ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraphSansMedium",
                        id: listHeaderId,
                        children: sectionHeader
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx("ul", {
                "aria-labelledby": listHeaderId,
                children: links.filter(function(link) {
                    return link.isVisible;
                }).map(function(param, i) {
                    var name = param.name, pathIdentifier = param.pathIdentifier, to = param.to;
                    return /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsx(CategoryLink, {
                            onClick: function() {
                                return trackCategoriesSideNavigationClick(pathIdentifier !== null && pathIdentifier !== void 0 ? pathIdentifier : "");
                            },
                            to: to,
                            children: /*#__PURE__*/ _jsx(CategoryText, {
                                children: name
                            })
                        })
                    }, to + i);
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "3x"
            })
        ]
    });
};
var CategoryText = styled(Span).withConfig({
    componentId: "sc-fdf31aac-1"
})([
    "&&&{-webkit-touch-callout:none;-webkit-user-select:none;user-select:none;padding-bottom:2px;font-weight:",
    ";border-bottom:",
    ";}"
], function(props) {
    return props.selected ? Weight.medium : Weight.book;
}, function(props) {
    return props.selected ? css([
        "1px solid ",
        ""
    ], Core.border.default) : 0;
});
var CategoryLink = styled(Link).withConfig({
    componentId: "sc-fdf31aac-2"
})([
    "display:block;width:100%;cursor:pointer !important;font-size:12px;line-height:32px;opacity:0.82;padding:0;"
]);
